import { useState, useEffect } from "react";
import { buildEdgeFunctionHeaders } from "@/lib/session";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BarChart3, LineChart, AreaChart, PieChart, Table, Hash, Loader2, Database } from "lucide-react";
import { cn } from "@/lib/utils";
import QueryPickerDialog from "./QueryPickerDialog";

const WIDGET_TYPES = [
  { id: "stat", label: "Stat Card", icon: Hash, description: "Single value with optional change" },
  { id: "bar", label: "Bar Chart", icon: BarChart3, description: "Compare categories" },
  { id: "line", label: "Line Chart", icon: LineChart, description: "Trends over time" },
  { id: "area", label: "Area Chart", icon: AreaChart, description: "Filled trends" },
  { id: "pie", label: "Pie Chart", icon: PieChart, description: "Proportions" },
  { id: "table", label: "Table", icon: Table, description: "Tabular data view" },
] as const;

type WidgetType = (typeof WIDGET_TYPES)[number]["id"];

interface AddWidgetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dashboardId?: string | null;
  onAdd: (widget: {
    type: WidgetType;
    title: string;
    queryId: string;
    xKey?: string;
    yKeys?: string[];
    width?: number;
    height?: number;
  }) => void;
}

/** Parse a raw execute/queries response into an array of row objects. */
const parseRows = (raw: unknown): Record<string, unknown>[] => {
  if (Array.isArray(raw)) {
    const first = raw[0];
    if (Array.isArray(first)) return first as Record<string, unknown>[];
    if (first && typeof first === "object" && ("rows" in first || "result" in first)) {
      return ((first as any).rows || (first as any).result || []) as Record<string, unknown>[];
    }
    return raw as Record<string, unknown>[];
  }
  if (raw && typeof raw === "object") {
    const r = raw as any;
    if (r.results) {
      const first = Array.isArray(r.results) ? r.results[0] : r.results;
      return Array.isArray(first) ? first : (first?.rows || first?.result || []);
    }
    if (r.rows) return r.rows;
  }
  return [];
};

const AddWidgetDialog = ({ open, onOpenChange, dashboardId, onAdd }: AddWidgetDialogProps) => {
  const [step, setStep] = useState<"type" | "config">("type");
  const [selectedType, setSelectedType] = useState<WidgetType | null>(null);
  const [title, setTitle] = useState("");
  const [queryId, setQueryId] = useState("");
  const [queryName, setQueryName] = useState("");
  const [selectedQuerySql, setSelectedQuerySql] = useState("");
  const [xKey, setXKey] = useState("");
  const [yKeys, setYKeys] = useState("");
  const [widgetWidth, setWidgetWidth] = useState("6");
  const [widgetHeight, setWidgetHeight] = useState("300");

  // Column autocomplete
  const [queryColumns, setQueryColumns] = useState<string[]>([]);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [xKeyFocused, setXKeyFocused] = useState(false);
  const [queryPickerOpen, setQueryPickerOpen] = useState(false);

  // Restore state from sessionStorage when returning from query page
  useEffect(() => {
    if (!open) return;
    const draftRaw = sessionStorage.getItem("widget_draft");
    const queryRaw = sessionStorage.getItem("widget_selected_query");
    if (draftRaw) {
      try {
        const draft = JSON.parse(draftRaw);
        setStep("config");
        setSelectedType(draft.selectedType || null);
        setTitle(draft.title || "");
        setWidgetWidth(draft.widgetWidth || "6");
        setWidgetHeight(draft.widgetHeight || "300");
        setXKey(draft.xKey || "");
        setYKeys(draft.yKeys || "");
      } catch { /* ignore */ }
      sessionStorage.removeItem("widget_draft");
    }
    if (queryRaw) {
      try {
        const query = JSON.parse(queryRaw);
        setQueryId(query.id);
        setQueryName(query.name || "");
        setSelectedQuerySql(query.sql_query || "");
      } catch { /* ignore */ }
      sessionStorage.removeItem("widget_selected_query");
    }
  }, [open]);

  // Fetch columns when selected query changes
  useEffect(() => {
    if (!selectedQuerySql) {
      setQueryColumns([]);
      return;
    }
    let cancelled = false;
    const fetchColumns = async () => {
      setLoadingColumns(true);
      try {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const headers = await buildEdgeFunctionHeaders();
        const sql = selectedQuerySql.trim().replace(/;\s*$/, "");
        const wrappedSql = /\bLIMIT\b/i.test(sql) ? sql : `${sql} LIMIT 1`;
        const res = await fetch(`${supabaseUrl}/functions/v1/mcp-api/execute/queries`, {
          method: "POST",
          headers,
          body: JSON.stringify([wrappedSql]),
        });
        const json = await res.json();
        const rows = parseRows(json);
        if (!cancelled && rows.length > 0) {
          setQueryColumns(Object.keys(rows[0]));
        } else if (!cancelled) {
          setQueryColumns([]);
        }
      } catch (err) {
        console.error("Failed to fetch query columns:", err);
        if (!cancelled) setQueryColumns([]);
      } finally {
        if (!cancelled) setLoadingColumns(false);
      }
    };
    fetchColumns();
    return () => { cancelled = true; };
  }, [selectedQuerySql]);

  const reset = () => {
    setStep("type");
    setSelectedType(null);
    setTitle("");
    setQueryId("");
    setQueryName("");
    setSelectedQuerySql("");
    setXKey("");
    setYKeys("");
    setWidgetWidth("6");
    setWidgetHeight("300");
    setQueryColumns([]);
  };

  const handleClose = (v: boolean) => {
    if (!v) reset();
    onOpenChange(v);
  };

  const handleTypeSelect = (type: WidgetType) => {
    setSelectedType(type);
    setStep("config");
  };

  const handleSelectQuery = () => {
    setQueryPickerOpen(true);
  };

  const handleQueryPicked = (query: { id: string; name: string; sql_query: string }) => {
    setQueryId(query.id);
    setQueryName(query.name);
    setSelectedQuerySql(query.sql_query);
    setQueryPickerOpen(false);
  };

  const yKeysArray = yKeys.split(",").map((k) => k.trim()).filter(Boolean);

  const toggleYKey = (col: string) => {
    if (yKeysArray.includes(col)) {
      setYKeys(yKeysArray.filter((k) => k !== col).join(", "));
    } else {
      setYKeys([...yKeysArray, col].join(", "));
    }
  };

  const handleAdd = () => {
    if (!selectedType || !title.trim() || !queryId) return;
    onAdd({
      type: selectedType,
      title: title.trim(),
      queryId,
      xKey: xKey || undefined,
      yKeys: yKeys ? yKeys.split(",").map((k) => k.trim()).filter(Boolean) : undefined,
      width: parseInt(widgetWidth),
      height: parseInt(widgetHeight),
    });
    reset();
    onOpenChange(false);
  };

  const needsAxisConfig = selectedType && ["bar", "line", "area"].includes(selectedType);

  const filteredXColumns = queryColumns.filter(
    (c) => !xKey || c.toLowerCase().includes(xKey.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {step === "type" ? "Choose Widget Type" : "Configure Widget"}
          </DialogTitle>
        </DialogHeader>

        {step === "type" && (
          <div className="grid grid-cols-3 gap-3 py-2">
            {WIDGET_TYPES.map((wt) => {
              const Icon = wt.icon;
              return (
                <button
                  key={wt.id}
                  onClick={() => handleTypeSelect(wt.id)}
                  className={cn(
                    "flex flex-col items-center gap-2 rounded-lg border border-border p-4 transition-colors hover:bg-accent hover:border-primary/50",
                    "text-center"
                  )}
                >
                  <Icon className="h-6 w-6 text-muted-foreground" />
                  <span className="text-sm font-medium">{wt.label}</span>
                  <span className="text-[10px] text-muted-foreground leading-tight">{wt.description}</span>
                </button>
              );
            })}
          </div>
        )}

        {step === "config" && (
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label>Widget Title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Revenue by Month"
                autoFocus
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>Width (columns)</Label>
                <Select value={widgetWidth} onValueChange={setWidgetWidth}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 — Quarter</SelectItem>
                    <SelectItem value="4">4 — Third</SelectItem>
                    <SelectItem value="6">6 — Half</SelectItem>
                    <SelectItem value="8">8 — Two-thirds</SelectItem>
                    <SelectItem value="12">12 — Full</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>Height (px)</Label>
                <Select value={widgetHeight} onValueChange={setWidgetHeight}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="200">200 — Small</SelectItem>
                    <SelectItem value="300">300 — Medium</SelectItem>
                    <SelectItem value="400">400 — Large</SelectItem>
                    <SelectItem value="500">500 — X-Large</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-1.5">
              <Label>Data Source</Label>
              {queryName ? (
                <div className="flex items-center gap-2 rounded-md border border-border px-3 py-2">
                  <Database className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate text-sm">{queryName}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleSelectQuery}
                  >
                    Change
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-start text-muted-foreground"
                  onClick={handleSelectQuery}
                >
                  <Database className="mr-2 h-4 w-4" />
                  Select Query
                </Button>
              )}
            </div>

            {needsAxisConfig && (
              <>
                <div className="grid gap-1.5">
                  <Label>
                    X-Axis Column
                    {loadingColumns && (
                      <Loader2 className="inline ml-2 h-3 w-3 animate-spin text-muted-foreground" />
                    )}
                  </Label>
                  <div className="relative">
                    <Input
                      value={xKey}
                      onChange={(e) => setXKey(e.target.value)}
                      onFocus={() => setXKeyFocused(true)}
                      onBlur={() => setTimeout(() => setXKeyFocused(false), 150)}
                      placeholder={
                        queryColumns.length > 0
                          ? "Type or select column…"
                          : "e.g. month, date, category"
                      }
                    />
                    {xKeyFocused && filteredXColumns.length > 0 && (
                      <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md max-h-32 overflow-y-auto">
                        {filteredXColumns.map((col) => (
                          <button
                            key={col}
                            type="button"
                            className={cn(
                              "w-full px-3 py-1.5 text-sm text-left hover:bg-accent",
                              col === xKey && "bg-accent font-medium"
                            )}
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setXKey(col);
                              setXKeyFocused(false);
                            }}
                          >
                            {col}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="grid gap-1.5">
                  <Label>Y-Axis Keys</Label>
                  <Input
                    value={yKeys}
                    onChange={(e) => setYKeys(e.target.value)}
                    placeholder={
                      queryColumns.length > 0
                        ? "Click columns below or type comma-separated…"
                        : "e.g. revenue, cost (comma-separated)"
                    }
                  />
                  {queryColumns.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {queryColumns.map((col) => {
                        const selected = yKeysArray.includes(col);
                        return (
                          <button
                            key={col}
                            type="button"
                            onClick={() => toggleYKey(col)}
                            className={cn(
                              "text-xs px-2 py-0.5 rounded-full border transition-colors",
                              selected
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-muted/50 hover:bg-accent border-border"
                            )}
                          >
                            {col}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}

            {selectedType === "pie" && (
              <p className="text-xs text-muted-foreground">
                Pie chart expects query results with "name" and "value" columns.
              </p>
            )}

            {selectedType === "stat" && (
              <p className="text-xs text-muted-foreground">
                Stat card will display the first value from the query result.
              </p>
            )}
          </div>
        )}

        {step === "config" && (
          <DialogFooter>
            <Button variant="outline" onClick={() => setStep("type")}>
              Back
            </Button>
            <Button onClick={handleAdd} disabled={!title.trim() || !queryId}>
              Add Widget
            </Button>
          </DialogFooter>
        )}
      </DialogContent>

      <QueryPickerDialog
        open={queryPickerOpen}
        onOpenChange={setQueryPickerOpen}
        onSelect={handleQueryPicked}
        linkType="dashboard"
        linkId={dashboardId || undefined}
      />
    </Dialog>
  );
};

export default AddWidgetDialog;
